import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Crown, Check, X, Send, TrendingUp, MessageSquare, Bell, RefreshCw, IndianRupee, Save } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth, API } from '../context/AuthContext';
import { AdminEmailStatus } from '../components/AdminEmailStatus';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const ADMIN_EMAILS = ['niranjanpujari31@gmail.com', 'gurusevakendra33@gmail.com'];

export default function AdminPage() {
  const { user, loading, login } = useAuth();
  const [tab, setTab] = useState('requests');
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifTag, setNotifTag] = useState('info');
  const [refreshing, setRefreshing] = useState(false);
  const [plans, setPlans] = useState([]);
  const [savingPlans, setSavingPlans] = useState(false);

  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const load = async () => {
    setRefreshing(true);
    try {
      const [s, r, u, p] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/premium-requests?status=all`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/plans`),
      ]);
      setStats(s.data);
      setRequests(r.data);
      setUsers(u.data);
      setPlans(p.data);
    } catch (e) { if (e?.response?.status !== 401 && e?.response?.status !== 403) console.warn('admin load failed', e); }
    setRefreshing(false);
  };

  const savePlans = async () => {
    const bad = plans.find(p => !Number.isInteger(Number(p.price)) || Number(p.price) < 1);
    if (bad) { toast({ title: 'ଭୁଲ ମୂଲ୍ୟ', description: `${bad.id}: ମୂଲ୍ୟ ≥ ₹1 ପୂର୍ଣ୍ଣ ସଂଖ୍ୟା ହେବା ଦରକାର` }); return; }
    setSavingPlans(true);
    try {
      const res = await axios.put(`${API}/admin/plans`, { plans: plans.map(p => ({ id: p.id, price: Number(p.price) })) }, { withCredentials: true });
      setPlans(res.data);
      toast({ title: '💰 ମୂଲ୍ୟ ସେଭ୍ ହେଲା', description: 'ନୂଆ ମୂଲ୍ୟ ଏବେ ସବୁ ଇଉଜରଙ୍କୁ ଦେଖାଯିବ' });
    } catch (e) { toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again' }); }
    setSavingPlans(false);
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const approve = async (id) => {
    try {
      await axios.post(`${API}/admin/premium-requests/${id}/approve`, {}, { withCredentials: true });
      toast({ title: '✅ Approved', description: 'ଇଉଜର ପ୍ରିମିୟମ୍ ହୋଇଗଲେ' });
      load();
    } catch { toast({ title: 'Approve failed' }); }
  };
  const reject = async (id) => {
    try {
      await axios.post(`${API}/admin/premium-requests/${id}/reject`, {}, { withCredentials: true });
      toast({ title: '❌ Rejected' });
      load();
    } catch { toast({ title: 'Reject failed' }); }
  };
  const sendNotif = async () => {
    if (!notifTitle || !notifBody) { toast({ title: 'Title ଓ body ଆବଶ୍ୟକ' }); return; }
    try {
      await axios.post(`${API}/admin/notifications`, { title: notifTitle, body: notifBody, tag: notifTag }, { withCredentials: true });
      setNotifTitle(''); setNotifBody('');
      toast({ title: '📢 Notification ପଠାଗଲା', description: 'ସମସ୍ତ ଇଉଜରଙ୍କୁ ମିଳିବ' });
    } catch { toast({ title: 'Failed to send' }); }
  };

  if (loading) return <div className="p-6 text-center text-sm text-gray-500">Loading...</div>;

  if (!user) {
    return (
      <div className="px-4 pt-8 text-center space-y-4">
        <Shield size={48} className="mx-auto text-gray-400" />
        <div className="font-bold text-gray-800">Admin ପାଇଁ Login ଆବଶ୍ୟକ</div>
        <Button onClick={login} className="bg-emerald-600 text-white">Login</Button>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="px-4 pt-8 text-center space-y-3">
        <Shield size={48} className="mx-auto text-red-400" />
        <div className="font-bold text-red-700">ପ୍ରବେଶ ନିଷେଧ</div>
        <p className="text-xs text-gray-600">କେବଳ Admin (gurusevakendra33@gmail.com) ପ୍ରବେଶ କରିପାରିବେ। ଆପଣ logged in: {user.email}</p>
        <Link to="/" className="inline-block text-emerald-600 font-semibold text-sm">← Home</Link>
      </div>
    );
  }

  return (
    <div className="px-4 pt-4 space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
          <ArrowLeft size={16} /> Home
        </Link>
        <button onClick={load} disabled={refreshing} className="text-emerald-600 flex items-center gap-1 text-xs font-bold">
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 60%, #0891b2 100%)' }}>
        <Shield size={24} />
        <h1 className="text-xl font-extrabold mt-1">Admin Panel</h1>
        <p className="text-xs text-white/90">Gurusevakendra ପରିଚାଳନା · {user.email}</p>
      </div>

      {stats && (
        <div className="grid grid-cols-3 gap-2">
          <Card className="rounded-xl p-3 text-center border-gray-200"><Users size={16} className="mx-auto text-blue-500" /><div className="text-lg font-extrabold text-gray-900 mt-0.5">{stats.users}</div><div className="text-[9px] text-gray-500">Users</div></Card>
          <Card className="rounded-xl p-3 text-center border-gray-200"><Crown size={16} className="mx-auto text-amber-500" /><div className="text-lg font-extrabold text-gray-900 mt-0.5">{stats.premium_users}</div><div className="text-[9px] text-gray-500">Premium</div></Card>
          <Card className="rounded-xl p-3 text-center border-gray-200"><TrendingUp size={16} className="mx-auto text-emerald-500" /><div className="text-lg font-extrabold text-gray-900 mt-0.5">{stats.pending_requests}</div><div className="text-[9px] text-gray-500">Pending</div></Card>
        </div>
      )}

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[['requests', 'Requests'], ['users', 'Users'], ['notify', 'Notify'], ['pricing', 'Pricing']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${tab === id ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'requests' && (
        <div className="space-y-2">
          {requests.length === 0 && <div className="text-center text-sm text-gray-500 py-6">କୌଣସି request ନାହିଁ</div>}
          {requests.map((r) => (
            <Card key={r.id} className="rounded-2xl p-3 border-gray-200">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-gray-900 truncate">{r.user_name || r.user_email}</div>
                  <div className="text-[11px] text-gray-500">{r.user_email}</div>
                  <div className="mt-1 flex flex-wrap gap-1 items-center text-[10px]">
                    <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-semibold uppercase">{r.plan}{r.amount ? ` · ₹${r.amount}` : ""}</span>
                    <span className={`px-1.5 py-0.5 rounded font-semibold uppercase ${r.status === 'pending' ? 'bg-amber-100 text-amber-700' : r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{r.status}</span>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-1">Txn: <span className="font-mono font-bold text-gray-800">{r.transaction_id || 'N/A'}</span></div>
                </div>
                {r.status === 'pending' && (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => approve(r.id)} className="p-1.5 rounded-lg bg-emerald-500 text-white"><Check size={14} /></button>
                    <button onClick={() => reject(r.id)} className="p-1.5 rounded-lg bg-red-500 text-white"><X size={14} /></button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div className="space-y-2">
          {users.map((u) => (
            <Card key={u.user_id} className="rounded-2xl p-3 border-gray-200 flex items-center gap-3">
              {u.picture ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold">{u.name?.[0] || 'U'}</div>}
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-gray-900 truncate">{u.name}</div>
                <div className="text-[10px] text-gray-500 truncate">{u.email}</div>
              </div>
              {u.is_premium && <Crown size={16} className="text-amber-500" />}
            </Card>
          ))}
        </div>
      )}

      {tab === 'notify' && (
        <Card className="rounded-2xl p-4 border-gray-200 space-y-3">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-800"><Bell size={16} /> Send Broadcast Notification</div>
          <Input value={notifTitle} onChange={(e) => setNotifTitle(e.target.value)} placeholder="Title" className="h-10 rounded-lg" />
          <Textarea value={notifBody} onChange={(e) => setNotifBody(e.target.value)} placeholder="Message body..." rows={3} className="rounded-lg" />
          <select value={notifTag} onChange={(e) => setNotifTag(e.target.value)} className="w-full h-10 border rounded-lg px-3 text-sm">
            <option value="info">Info</option>
            <option value="welcome">Welcome</option>
            <option value="navodaya">Navodaya</option>
            <option value="board">Board</option>
            <option value="premium">Premium</option>
          </select>
          <Button onClick={sendNotif} className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2"><Send size={14} /> Broadcast</Button>
        </Card>
      )}
      {tab === 'notify' && <AdminEmailStatus />}
      {tab === 'pricing' && (
        <Card className="rounded-2xl p-4 border-gray-200 space-y-3" data-testid="admin-pricing-card">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-800"><IndianRupee size={16} /> Subscription ମୂଲ୍ୟ (₹)</div>
          {plans.map((p) => (
            <label key={p.id} className="flex items-center gap-3">
              <span className="w-24 text-xs font-semibold text-gray-700 capitalize">{p.id} <span className="text-gray-400">({p.days}d)</span></span>
              <Input type="number" min={1} step={1} value={p.price} data-testid={`admin-price-${p.id}`}
                onChange={(e) => setPlans(ps => ps.map(x => x.id === p.id ? { ...x, price: e.target.value } : x))}
                className="h-10 rounded-lg flex-1" />
            </label>
          ))}
          <Button onClick={savePlans} disabled={savingPlans} data-testid="admin-save-prices-button" className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2">
            <Save size={14} /> {savingPlans ? 'Saving…' : 'ମୂଲ୍ୟ ସେଭ୍ କରନ୍ତୁ'}
          </Button>
          <p className="text-[10px] text-gray-500">ସେଭ୍ କରିବା ପରେ /payment ପୃଷ୍ଠା ଓ ହୋମ୍ ପେଜରେ ନୂଆ ମୂଲ୍ୟ ଦେଖାଯିବ। ପୂର୍ବ request ଗୁଡ଼ିକର ମୂଲ୍ୟ ବଦଳିବ ନାହିଁ।</p>
        </Card>
      )}
    </div>
  );
}
